/**
 * @fileoverview Pre-Appointment pipeline surface (MOR-882)
 *
 * The workspace pipeline-management surface for pre-appointment jobs:
 * a filter bar (stage / labels / referrer / type / search / show-closed),
 * saveable personal views, and a board ⇄ list toggle — mirroring the task
 * surface (`src/calendar/task-surface.tsx`) so the two workspaces feel the
 * same. Owns the filter/view/group/sort state and the job side panel;
 * data rides `api.preAppointment.*`.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { convexQueryOrIdle } from "@/lib/convex-query-idle";
import { useMutation } from "convex/react";
import { toast } from "sonner";
import {
  LayoutList,
  Search,
  SlidersHorizontal,
  SquareKanban,
  X,
} from "lucide-react";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import type {
  PreAppointmentViewGroupBy,
  PreAppointmentViewSortBy,
} from "@cvx/preAppointment/preAppointmentViews/schema";

import { APPOINTMENT_TYPE_OPTIONS, type PipelineJob } from "./constants";
import { PipelineBoard, type PipelineBoardMoveArgs } from "./pipeline-board";
import { PipelineList, type PipelineSortDir } from "./pipeline-list";
import { PipelineJobPanel } from "./pipeline-job-panel";
import { FirmContactDetailPanel } from "@/components/firm-contacts/FirmContactDetailPanel";
import { ContactDetailPanel } from "@/components/jobs/phases/contacts/ContactDetailPanel";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/ui/composed/menus/dropdown-menu";
import { PipelineViewSwitcher } from "./pipeline-view-switcher";
import {
  emptyPipelineFilters,
  type PipelineFilterState,
  type PipelineViewConfig,
} from "./pipeline-view-config";
import { Badge, type BadgeColor } from "@/ui/primitives/badge";
import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import { Spinner } from "@/ui/primitives/spinner";
import { Switch } from "@/ui/primitives/switch";
import { Label } from "@/ui/primitives/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import { ToggleGroup, ToggleGroupItem } from "@/ui/primitives/toggle-group";
import { MultiSearchableSelect } from "@/ui/composed/inputs/multi-searchable-select";
import { extractErrorMessage } from "@/utils/errors";

const GROUP_BY_OPTIONS: Array<{
  value: PreAppointmentViewGroupBy;
  label: string;
}> = [
  { value: "status", label: "Stage" },
  { value: "referrer", label: "Referrer" },
  { value: "appointmentType", label: "Job type" },
  { value: "none", label: "No grouping" },
];

const SORT_BY_OPTIONS: Array<{
  value: PreAppointmentViewSortBy;
  label: string;
}> = [
  { value: "created", label: "Created" },
  { value: "name", label: "Name" },
  { value: "status", label: "Stage" },
  { value: "manual", label: "Manual (board order)" },
];

/** Per-viewer custom-field visibility (MOR-948). */
const HIDDEN_FIELDS_KEY = "morgan.pipeline.hiddenCustomFields";

export function PipelineSurface({
  orgId,
  canEdit,
}: {
  orgId: Id<"organizations">;
  /** Firm-directory writes (adding a referrer) are owner/admin only. */
  canEdit: boolean;
}) {
  const moveOnBoard = useMutation(api.preAppointment.pipeline.moveOnBoard);
  const ensureDefaults = useMutation(
    api.preAppointment.statuses.ensureDefaults,
  );

  // Seed the default pipeline stages/labels once per mount (idempotent).
  const seeded = useRef(false);
  useEffect(() => {
    if (seeded.current) return;
    seeded.current = true;
    void ensureDefaults({ orgId }).catch(() => {
      // Non-fatal: the surface still renders; stages just stay empty.
    });
  }, [ensureDefaults, orgId]);

  const [filters, setFilters] =
    useState<PipelineFilterState>(emptyPipelineFilters);
  const [view, setView] = useState<"board" | "list">("board");
  const [groupBy, setGroupBy] = useState<PreAppointmentViewGroupBy>("status");
  const [sortBy, setSortBy] = useState<PreAppointmentViewSortBy>("manual");
  const [sortDir, setSortDir] = useState<PipelineSortDir>("asc");
  const [panelJobId, setPanelJobId] = useState<Id<"jobs"> | null>(null);
  // Referrer drawer — the same firm-contact panel the directory opens, so a
  // referrer's details expand from the side wherever their name appears.
  const [referrerPanelId, setReferrerPanelId] =
    useState<Id<"firmContacts"> | null>(null);
  // Job-contact drawer — the job CRM's own panel, so a director's record and
  // its correspondence open from the pipeline. Carries the jobId alongside the
  // contact so the drawer survives the job panel being closed behind it.
  const [contactPanel, setContactPanel] = useState<{
    jobId: Id<"jobs">;
    contactId: Id<"jobContacts">;
  } | null>(null);

  const { data: jobs } = useQuery(
    convexQuery(api.preAppointment.pipeline.listPipelineJobs, {
      orgId,
      includeClosed: filters.includeClosed,
    }),
  );
  const { data: statuses } = useQuery(
    convexQuery(api.preAppointment.statuses.listByOrg, { orgId }),
  );
  const { data: labels } = useQuery(
    convexQuery(api.preAppointment.labels.listByOrg, { orgId }),
  );
  // Firm-defined custom fields (MOR-948) — their values ride along on each
  // job, so the board and list need the definitions to style the badges.
  const { data: customFields } = useQuery(
    convexQuery(api.preAppointment.fields.listByOrg, { orgId }),
  );
  // Which custom-field columns/badges are hidden. A per-viewer preference
  // (not part of the shared saved view), so it lives in localStorage and
  // degrades to "show everything" when storage is unavailable.
  const [hiddenFieldIds, setHiddenFieldIds] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(HIDDEN_FIELDS_KEY);
      return raw ? (JSON.parse(raw) as string[]) : [];
    } catch {
      return [];
    }
  });
  const toggleFieldVisibility = (fieldId: string) => {
    setHiddenFieldIds((prev) => {
      const next = prev.includes(fieldId)
        ? prev.filter((id) => id !== fieldId)
        : [...prev, fieldId];
      try {
        localStorage.setItem(HIDDEN_FIELDS_KEY, JSON.stringify(next));
      } catch {
        // A viewer with storage blocked just loses the persistence.
      }
      return next;
    });
  };
  const visibleCustomFields = (customFields ?? []).filter(
    (f) => !hiddenFieldIds.includes(String(f._id)),
  );
  const { data: referrers } = useQuery(
    convexQuery(api.preAppointment.pipeline.listReferrers, { orgId }),
  );
  // Siblings of the contact in the drawer — the detail panel's merge picker
  // needs the job's other rows. Only fetched while the drawer is open.
  const { data: contactPanelSiblings } = useQuery(
    convexQueryOrIdle(
      api.contacts.queries.listJobContacts,
      contactPanel ? { jobId: contactPanel.jobId } : undefined,
    ),
  );

  const statusOptions = useMemo(
    () =>
      (statuses ?? []).map((s) => ({
        value: String(s._id),
        label: s.name,
        color: s.color as BadgeColor,
      })),
    [statuses],
  );
  const labelOptions = useMemo(
    () =>
      (labels ?? []).map((l) => ({
        value: String(l._id),
        label: l.name,
        color: l.color as BadgeColor,
      })),
    [labels],
  );
  const referrerOptions = useMemo(
    () =>
      // MOR-995 — nested people render as "Person — Firm" in the filter.
      (referrers ?? []).map((r) => ({
        value: String(r._id),
        label: r.parentDisplayName
          ? `${r.displayName} — ${r.parentDisplayName}`
          : r.displayName,
      })),
    [referrers],
  );

  const filteredJobs = useMemo<PipelineJob[]>(() => {
    const predicates: Array<(j: PipelineJob) => boolean> = [];
    if (filters.statusIds.length > 0) {
      const set = new Set(filters.statusIds);
      predicates.push(
        (j) =>
          j.preAppointmentStatusId !== null &&
          set.has(String(j.preAppointmentStatusId)),
      );
    }
    if (filters.labelIds.length > 0) {
      const set = new Set(filters.labelIds);
      predicates.push((j) =>
        j.preAppointmentLabelIds.some((id) => set.has(String(id))),
      );
    }
    if (filters.referrerIds.length > 0) {
      const set = new Set(filters.referrerIds);
      predicates.push(
        (j) => Boolean(j.referrer) && set.has(String(j.referrer?._id)),
      );
    }
    if (filters.appointmentTypes.length > 0) {
      const set = new Set(filters.appointmentTypes);
      predicates.push(
        (j) => j.appointmentType != null && set.has(j.appointmentType),
      );
    }
    const needle = filters.search.trim().toLowerCase();
    if (needle) {
      predicates.push(
        (j) =>
          j.entityName.toLowerCase().includes(needle) ||
          (j.jobName ?? "").toLowerCase().includes(needle),
      );
    }
    return (jobs ?? []).filter((j) => predicates.every((p) => p(j)));
  }, [jobs, filters]);

  // Keep the panel's job fresh as the reactive query updates.
  const panelJob = useMemo(
    () =>
      panelJobId
        ? ((jobs ?? []).find((j) => j._id === panelJobId) ?? null)
        : null,
    [jobs, panelJobId],
  );

  const currentConfig: PipelineViewConfig = {
    filters,
    viewType: view,
    groupBy,
    sortBy,
  };

  const applyConfig = (config: PipelineViewConfig) => {
    setFilters(config.filters);
    setView(config.viewType);
    setGroupBy(config.groupBy);
    setSortBy(config.sortBy);
    setSortDir("asc");
  };

  const handleMove = async (args: PipelineBoardMoveArgs) => {
    try {
      await moveOnBoard({
        jobId: args.jobId,
        statusId: args.statusId,
        beforeJobId: args.beforeJobId,
        afterJobId: args.afterJobId,
      });
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not move the job."));
      throw err;
    }
  };

  if (jobs === undefined || statuses === undefined) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner className="text-muted-foreground" />
      </div>
    );
  }

  const hasFilters =
    filters.statusIds.length > 0 ||
    filters.labelIds.length > 0 ||
    filters.referrerIds.length > 0 ||
    filters.appointmentTypes.length > 0 ||
    filters.search.trim().length > 0;

  return (
    <>
      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={filters.search}
            onChange={(e) =>
              setFilters((f) => ({ ...f, search: e.target.value }))
            }
            placeholder="Search jobs…"
            className="h-9 w-56 pl-8"
          />
        </div>
        <MultiSearchableSelect
          value={filters.statusIds}
          onValueChange={(statusIds) =>
            setFilters((f) => ({ ...f, statusIds }))
          }
          options={statusOptions}
          placeholder="Stage"
          className="h-9 w-auto min-w-28"
        />
        <MultiSearchableSelect
          value={filters.labelIds}
          onValueChange={(labelIds) => setFilters((f) => ({ ...f, labelIds }))}
          options={labelOptions}
          placeholder="Labels"
          className="h-9 w-auto min-w-28"
        />
        <MultiSearchableSelect
          value={filters.referrerIds}
          onValueChange={(referrerIds) =>
            setFilters((f) => ({ ...f, referrerIds }))
          }
          options={referrerOptions}
          placeholder="Referrer"
          className="h-9 w-auto min-w-28"
        />
        <MultiSearchableSelect
          value={filters.appointmentTypes}
          onValueChange={(appointmentTypes) =>
            setFilters((f) => ({ ...f, appointmentTypes }))
          }
          options={[...APPOINTMENT_TYPE_OPTIONS]}
          placeholder="Job type"
          className="h-9 w-auto min-w-28"
        />
        <div className="flex items-center gap-1.5 pl-1">
          <Switch
            id="pipeline-show-closed"
            checked={filters.includeClosed}
            onCheckedChange={(includeClosed) =>
              setFilters((f) => ({ ...f, includeClosed }))
            }
          />
          <Label
            htmlFor="pipeline-show-closed"
            className="text-xs text-muted-foreground"
          >
            Show converted &amp; lost
          </Label>
        </div>
        {hasFilters && (
          <Button
            variant="ghost"
            size="sm"
            className="h-9 gap-1 text-muted-foreground"
            onClick={() =>
              setFilters((f) => ({
                ...emptyPipelineFilters(),
                includeClosed: f.includeClosed,
              }))
            }
          >
            <X className="size-3.5" />
            Clear
          </Button>
        )}

        <div className="ml-auto flex items-center gap-2">
          <Select
            value={groupBy}
            onValueChange={(v) => setGroupBy(v as PreAppointmentViewGroupBy)}
          >
            <SelectTrigger className="h-9 w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {GROUP_BY_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  Group: {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {view === "list" && (
            <Select
              value={sortBy}
              onValueChange={(v) => setSortBy(v as PreAppointmentViewSortBy)}
            >
              <SelectTrigger className="h-9 w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SORT_BY_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    Sort: {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {(customFields ?? []).length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-9 gap-1.5">
                  <SlidersHorizontal className="size-3.5" />
                  Fields
                  {hiddenFieldIds.length > 0 && (
                    <span className="text-xs text-muted-foreground">
                      ({visibleCustomFields.length}/
                      {(customFields ?? []).length})
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuLabel>Custom fields</DropdownMenuLabel>
                {(customFields ?? []).map((field) => (
                  <DropdownMenuCheckboxItem
                    key={String(field._id)}
                    checked={!hiddenFieldIds.includes(String(field._id))}
                    onCheckedChange={() =>
                      toggleFieldVisibility(String(field._id))
                    }
                    onSelect={(e) => e.preventDefault()}
                  >
                    {field.name}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <PipelineViewSwitcher
            orgId={orgId}
            current={currentConfig}
            onApply={applyConfig}
          />
          <ToggleGroup
            type="single"
            value={view}
            onValueChange={(v) => {
              if (v === "board" || v === "list") setView(v);
            }}
            variant="outline"
            size="sm"
          >
            <ToggleGroupItem value="list" aria-label="List view">
              <LayoutList className="size-3.5" />
            </ToggleGroupItem>
            <ToggleGroupItem value="board" aria-label="Board view">
              <SquareKanban className="size-3.5" />
            </ToggleGroupItem>
          </ToggleGroup>
        </div>
      </div>

      {/* Result count when filtering */}
      {hasFilters && (
        <div className="flex items-center gap-2">
          <Badge variant="secondary" size="sm">
            {filteredJobs.length} of {jobs.length} job
            {jobs.length === 1 ? "" : "s"}
          </Badge>
        </div>
      )}

      {view === "board" ? (
        <PipelineBoard
          jobs={filteredJobs}
          statuses={statuses}
          labels={labels ?? []}
          customFields={visibleCustomFields}
          groupBy={groupBy}
          onCardClick={(job) => setPanelJobId(job._id)}
          onMove={handleMove}
          onOpenReferrer={setReferrerPanelId}
        />
      ) : (
        <PipelineList
          jobs={filteredJobs}
          statuses={statuses}
          labels={labels ?? []}
          customFields={visibleCustomFields}
          groupBy={groupBy}
          sortBy={sortBy}
          sortDir={sortDir}
          onSortChange={(nextSortBy, nextDir) => {
            setSortBy(nextSortBy);
            setSortDir(nextDir);
          }}
          onRowClick={(job) => setPanelJobId(job._id)}
          onOpenReferrer={setReferrerPanelId}
        />
      )}

      <PipelineJobPanel
        job={panelJob}
        statuses={statuses}
        labels={labels ?? []}
        referrers={referrers ?? []}
        onClose={() => setPanelJobId(null)}
        onOpenReferrer={setReferrerPanelId}
        onOpenContact={(contactId) => {
          if (panelJob) setContactPanel({ jobId: panelJob._id, contactId });
        }}
        orgId={orgId}
        canCreateReferrer={canEdit}
      />

      <FirmContactDetailPanel
        firmContactId={referrerPanelId}
        open={referrerPanelId !== null}
        canEdit={canEdit}
        onClose={() => setReferrerPanelId(null)}
      />

      {contactPanel && (
        <ContactDetailPanel
          jobId={contactPanel.jobId}
          contactId={contactPanel.contactId}
          open
          onClose={() => setContactPanel(null)}
          jobContacts={contactPanelSiblings ?? []}
        />
      )}
    </>
  );
}
